import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Mail, Box } from "lucide-react";

function fmtCuando(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const ayer = new Date(today); ayer.setDate(ayer.getDate() - 1);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    if (sameDay(d, today)) return `Hoy ${hh}:${mm}`;
    if (sameDay(d, ayer)) return `Ayer ${hh}:${mm}`;
    const dd = String(d.getDate()).padStart(2, "0");
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mo} ${hh}:${mm}`;
}

const ICON = { paquete: Package, correspondencia: Mail, otro: Box };
const TIPO_LABEL = { paquete: "Paquete", correspondencia: "Correspondencia", otro: "Otro" };

export default function MisPaquetes() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/paqueteria").then((r) => setItems(r.data)); }, []);

    const pendientes = useMemo(() => items.filter((p) => p.estado === "pendiente"), [items]);
    const retirados = useMemo(() => items.filter((p) => p.estado === "retirado"), [items]);

    const renderItem = (p) => {
        const Icon = ICON[p.tipo] || Box;
        const isPend = p.estado === "pendiente";
        return (
            <Card key={p.paquete_id} className={isPend ? "" : "opacity-80"}>
                <CardContent className="p-3 flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 ${isPend ? "bg-accent/15 text-accent" : "bg-success/15 text-success"}`}>
                        <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm leading-tight">{TIPO_LABEL[p.tipo] || "Paquete"}{p.empresa_origen ? ` · ${p.empresa_origen}` : ""}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                            {fmtCuando(p.creado_en)}
                            {p.destinatario_nombre ? ` · Para ${p.destinatario_nombre}` : ""}
                        </p>
                        {p.observacion && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.observacion}</p>}
                        {p.foto_url && (
                            <img src={p.foto_url} alt="paquete" className="mt-2 rounded-md border border-border max-h-32" />
                        )}
                    </div>
                    <div className="shrink-0 text-right">
                        {isPend ? (
                            <Badge className="bg-accent/15 text-accent border border-accent/30 hover:bg-accent/15 text-[10px]">
                                Pendiente de retiro
                            </Badge>
                        ) : (
                            <Badge className="bg-success/15 text-success border border-success/40 hover:bg-success/15 text-[10px]">
                                Retirado · {fmtCuando(p.fecha_retiro)}
                            </Badge>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
        <div>
            <PageHeader title="Mis paquetes" subtitle="Lo que llega a tu unidad y queda en recepción." />

            {items.length === 0 ? (
                <EmptyState title="No hay paquetes" description="Cuando recepción reciba algo para tu unidad, lo vas a ver acá." />
            ) : (
                <div className="space-y-4">
                    {pendientes.length > 0 && (
                        <div>
                            <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground mb-2">Pendientes ({pendientes.length})</h3>
                            <div className="grid gap-2">{pendientes.map(renderItem)}</div>
                        </div>
                    )}
                    {retirados.length > 0 && (
                        <div>
                            <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground mb-2">Historial</h3>
                            <div className="grid gap-2">{retirados.map(renderItem)}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
